import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material'
import { X, Mic, Sparkles, Check } from 'lucide-react'
import { Platform } from 'graphql/generated/graphql'
import { LOCAL, DEVELOP } from 'constants/env'
import AnimatedLoadingText from 'components/AnimatedLoadingText/AnimatedLoadingText'

const config = process.env.NODE_ENV === 'development' ? LOCAL : DEVELOP

interface Episode {
  id: number
  title: string
  description: string
  datePublished: number
  duration: number
  image: string
  enclosureUrl: string
}

const PLATFORM_OPTIONS: Array<{ value: Platform; label: string; emoji: string }> = [
  { value: Platform.Instagram, label: 'Instagram', emoji: '📷' },
  { value: Platform.TikTok, label: 'TikTok', emoji: '🎵' },
  { value: Platform.Youtube, label: 'YouTube', emoji: '🎥' },
  { value: Platform.Reddit, label: 'Reddit', emoji: '🔴' },
  { value: Platform.Twitter, label: 'Twitter', emoji: '🐦' },
]

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  if (mins < 60) return `${mins}m`
  const hrs = Math.floor(mins / 60)
  const remainMins = mins % 60
  return `${String(hrs)}h ${String(remainMins)}m`
}

function formatDate(epoch: number): string {
  return new Date(epoch * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

interface PromoteEpisodeDialogProps {
  open: boolean
  onClose: () => void
  onCreated: () => void
}

export default function PromoteEpisodeDialog({ open, onClose, onCreated }: PromoteEpisodeDialogProps): JSX.Element {
  const [episodes, setEpisodes] = useState<Episode[]>([])
  const [podcastName, setPodcastName] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedEpisode, setSelectedEpisode] = useState<Episode | null>(null)
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([Platform.Instagram, Platform.TikTok])
  const [creating, setCreating] = useState(false)
  const [created, setCreated] = useState(false)

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setSelectedEpisode(null)
    setCreated(false)

    fetch(`${config.API_URL}/api/podcast/my-episodes`, { credentials: 'include' })
      .then(async (res) => {
        const data = await res.json()
        if (data.success) {
          setEpisodes(data.data.episodes ?? [])
          setPodcastName(data.data.podcastName ?? null)
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [open])

  const handlePromote = async (): Promise<void> => {
    if (!selectedEpisode) return
    setCreating(true)
    try {
      const response = await fetch(`${config.API_URL}/api/podcast/promote-episode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          episodeTitle: selectedEpisode.title,
          episodeDescription: selectedEpisode.description,
          audioUrl: selectedEpisode.enclosureUrl || undefined,
          platforms: selectedPlatforms,
        }),
      })
      const data = await response.json()
      if (data.success) {
        setCreated(true)
        setTimeout(() => {
          onClose()
          onCreated()
        }, 1500)
      }
    } catch (error) {
      console.error('Promote episode error:', error)
    } finally {
      setCreating(false)
    }
  }

  const togglePlatform = (p: Platform): void => {
    setSelectedPlatforms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{
        sx: {
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(24px)',
          border: { xs: 'none', sm: '1px solid rgba(168, 85, 247, 0.2)' },
          borderRadius: { xs: 0, sm: '16px' },
          color: 'white',
          m: { xs: 0, sm: 2 },
          maxHeight: '80vh',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Mic size={20} color="#a855f7" />
          <Typography sx={{ fontWeight: 600, fontSize: { xs: '16px', sm: '18px' } }}>
            Promote an Episode
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#a855f7' }} />
          </Box>
        )}

        {!loading && episodes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {podcastName ? 'No episodes found.' : 'Connect your podcast first via the onboarding flow.'}
            </Typography>
          </Box>
        )}

        {/* Episode list */}
        {!loading && !selectedEpisode && episodes.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {podcastName && (
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', mb: 1 }}>
                Recent episodes from {podcastName}
              </Typography>
            )}
            {episodes.map((ep) => (
              <Box
                key={ep.id}
                onClick={() => setSelectedEpisode(ep)}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  p: 1.5,
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  '&:hover': {
                    backgroundColor: 'rgba(168,85,247,0.08)',
                    borderColor: 'rgba(168,85,247,0.3)',
                  },
                }}
              >
                {ep.image && (
                  <Box component="img" src={ep.image} sx={{ width: 44, height: 44, borderRadius: '8px', objectFit: 'cover', flexShrink: 0 }} />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '13px', mb: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ep.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                      {formatDate(ep.datePublished)}
                    </Typography>
                    {ep.duration > 0 && (
                      <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px' }}>
                        {formatDuration(ep.duration)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Selected episode + platform picker */}
        {selectedEpisode && !created && (
          <Box>
            {/* Selected episode preview */}
            <Box sx={{ p: 2, mb: 2, backgroundColor: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.2)', borderRadius: '12px' }}>
              <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '14px', mb: 0.5 }}>
                {selectedEpisode.title}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {selectedEpisode.description.replace(/<[^>]*>/g, '')}
              </Typography>
            </Box>

            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '13px', mb: 1 }}>
              Find conversations about this episode on:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 3 }}>
              {PLATFORM_OPTIONS.map((p) => (
                <Chip
                  key={p.value}
                  label={`${p.emoji} ${p.label}`}
                  onClick={() => togglePlatform(p.value)}
                  sx={{
                    backgroundColor: selectedPlatforms.includes(p.value) ? 'rgba(168,85,247,0.3)' : 'rgba(255,255,255,0.05)',
                    border: selectedPlatforms.includes(p.value) ? '1px solid rgba(168,85,247,0.5)' : '1px solid rgba(255,255,255,0.1)',
                    color: 'white',
                    '&:hover': { backgroundColor: 'rgba(168,85,247,0.2)' },
                  }}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={() => setSelectedEpisode(null)}
                sx={{ color: 'rgba(255,255,255,0.5)', textTransform: 'none', flex: 1 }}
              >
                Back
              </Button>
              <Button
                onClick={() => { handlePromote().catch(console.error) }}
                disabled={creating || selectedPlatforms.length === 0}
                startIcon={creating ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Sparkles size={16} />}
                sx={{
                  background: 'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
                  color: 'white',
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 2,
                }}
              >
                {creating ? <AnimatedLoadingText messages="Creating episode signal" fontSize="14px" color="white" /> : 'Create Episode Signal'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Success */}
        {created && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Check size={24} color="#22c55e" />
            </Box>
            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '16px', mb: 0.5 }}>
              Episode signal created
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
              Scanning for conversations about this episode...
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
